// ─────────────────────────────────────────────────────────────────────────────
// Tema de marca por entidad
// Dos colores configurables (primario/secundario) que se exponen como variables
// CSS globales para que toda la app (sidebar, navbar, botones, modales, kioskos)
// los tome como referencia.
// ─────────────────────────────────────────────────────────────────────────────

export const COLOR_PRIMARIO_DEFECTO = '#0d7367';
export const COLOR_SECUNDARIO_DEFECTO = '#14b8a6';

// Paleta de presets que se ofrecen en la configuración de la empresa.
export const PRESETS_COLORES = [
    { primario: '#0d7367', secundario: '#14b8a6', nombre: 'Teal' },
    { primario: '#1e40af', secundario: '#3b82f6', nombre: 'Azul' },
    { primario: '#4338ca', secundario: '#8b5cf6', nombre: 'Índigo' },
    { primario: '#7c2d12', secundario: '#f97316', nombre: 'Naranja' },
    { primario: '#7f1d1d', secundario: '#ef4444', nombre: 'Rojo' },
    { primario: '#14532d', secundario: '#22c55e', nombre: 'Verde' },
    { primario: '#831843', secundario: '#ec4899', nombre: 'Rosa' },
    { primario: '#0f172a', secundario: '#64748b', nombre: 'Grafito' },
];

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Devuelve un hex normalizado (#rrggbb) o null si no es válido.
export const normalizarHex = (valor) => {
    if (!valor || typeof valor !== 'string') return null;
    const limpio = valor.trim();
    if (!HEX_RE.test(limpio)) return null;
    let hex = limpio.replace('#', '');
    if (hex.length === 3) {
        hex = hex.split('').map((c) => c + c).join('');
    }
    return `#${hex.toLowerCase()}`;
};

export const hexToRgb = (hex) => {
    const h = normalizarHex(hex) || COLOR_PRIMARIO_DEFECTO;
    const valor = h.replace('#', '');
    return [
        parseInt(valor.substring(0, 2), 16),
        parseInt(valor.substring(2, 4), 16),
        parseInt(valor.substring(4, 6), 16),
    ];
};

// Colores de texto claros/oscuros que se alternan según el contraste del fondo.
export const TEXTO_CLARO = '#ffffff';
export const TEXTO_OSCURO = '#0f172a';

// Umbral de luminosidad percibida (YIQ): por encima se usa texto oscuro.
// Está calibrado para que toda la paleta por defecto (colores medios/oscuros)
// mantenga el texto blanco actual, y sólo los colores realmente claros pasen a
// texto oscuro.
const YIQ_UMBRAL = 150;

// Luminosidad percibida (YIQ) de un color hex.
const yiq = (hex) => {
    const h = normalizarHex(hex) || COLOR_PRIMARIO_DEFECTO;
    const v = h.replace('#', '');
    const r = parseInt(v.substring(0, 2), 16);
    const g = parseInt(v.substring(2, 4), 16);
    const b = parseInt(v.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
};

// Luminancia relativa (WCAG) de un color hex.
const luminanciaRelativa = (hex) => {
    const h = normalizarHex(hex) || COLOR_PRIMARIO_DEFECTO;
    const v = h.replace('#', '');
    const canal = (par) => {
        const n = parseInt(par, 16) / 255;
        return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * canal(v.substring(0, 2))
        + 0.7152 * canal(v.substring(2, 4))
        + 0.0722 * canal(v.substring(4, 6));
};

// Ratio de contraste WCAG entre dos luminancias (1 a 21).
const ratioContraste = (l1, l2) => {
    const mayor = Math.max(l1, l2);
    const menor = Math.min(l1, l2);
    return (mayor + 0.05) / (menor + 0.05);
};

// Devuelve el color de texto (claro u oscuro) legible sobre `fondo`.
// Evita el clásico texto blanco sobre un color claro (ilegible).
export const contrasteTexto = (fondo) =>
    (yiq(fondo) >= YIQ_UMBRAL ? TEXTO_OSCURO : TEXTO_CLARO);

// Texto legible sobre un degradado entre dos colores: se decide con el promedio
// de luminosidad de ambos extremos.
export const contrasteGradiente = (colorA, colorB) =>
    (((yiq(colorA) + yiq(colorB)) / 2) >= YIQ_UMBRAL ? TEXTO_OSCURO : TEXTO_CLARO);

export const rgbToHex = ([r, g, b]) =>
    `#${[r, g, b].map((n) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0')).join('')}`;

// Mezcla el color hacia blanco (porcentaje 0-100).
export const aclarar = (hex, pct) => {
    const mezcla = Math.max(0, Math.min(100, pct)) / 100;
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex([r + (255 - r) * mezcla, g + (255 - g) * mezcla, b + (255 - b) * mezcla]);
};

// Mezcla el color hacia negro (porcentaje 0-100).
export const oscurecer = (hex, pct) => {
    const mezcla = Math.max(0, Math.min(100, pct)) / 100;
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex([r * (1 - mezcla), g * (1 - mezcla), b * (1 - mezcla)]);
};

// Color de marca legible como TEXTO/ícono sobre fondos claros (ej: blanco).
// Si el color elegido es muy claro, se oscurece por pasos hasta tener contraste
// suficiente con blanco. Para los colores oscuros (paleta por defecto) devuelve
// el mismo color, así que no cambia el look actual.
export const colorTextoMarca = (hex, contrasteMinimo = 4.5) => {
    let color = normalizarHex(hex) || COLOR_PRIMARIO_DEFECTO;
    const lumBlanco = luminanciaRelativa('#ffffff');
    let pct = 0;
    while (ratioContraste(lumBlanco, luminanciaRelativa(color)) < contrasteMinimo && pct < 90) {
        pct += 6;
        color = oscurecer(color, 6);
    }
    return color;
};

// Aplica los dos colores como variables CSS en :root.
export const aplicarTema = (colorPrimario, colorSecundario) => {
    if (typeof document === 'undefined') return;
    const primario = normalizarHex(colorPrimario) || COLOR_PRIMARIO_DEFECTO;
    const secundario = normalizarHex(colorSecundario) || COLOR_SECUNDARIO_DEFECTO;

    const root = document.documentElement;
    const set = (k, v) => root.style.setProperty(k, v);

    set('--color-primario', primario);
    set('--color-primario-rgb', hexToRgb(primario).join(', '));
    set('--color-primario-oscuro', oscurecer(primario, 14));
    set('--color-primario-suave', aclarar(primario, 92));
    set('--color-primario-borde', aclarar(primario, 68));
    set('--color-primario-claro', aclarar(primario, 30));
    // Color de letra legible sobre el color primario (blanco o casi negro)
    set('--color-primario-contraste', contrasteTexto(primario));
    // Color primario legible como TEXTO sobre fondos claros (navbar, títulos…)
    set('--color-primario-texto', colorTextoMarca(primario));

    set('--color-secundario', secundario);
    set('--color-secundario-rgb', hexToRgb(secundario).join(', '));
    set('--color-secundario-oscuro', oscurecer(secundario, 14));
    set('--color-secundario-suave', aclarar(secundario, 88));
    set('--color-secundario-borde', aclarar(secundario, 60));
    // Color de letra legible sobre el color secundario (blanco o casi negro)
    set('--color-secundario-contraste', contrasteTexto(secundario));

    // Color de letra legible sobre el degradado de marca (primario → secundario)
    set('--color-marca-contraste', contrasteGradiente(primario, secundario));

    // Superficies del sidebar
    const sidebar = oscurecer(primario, 6);
    set('--color-sidebar', sidebar);
    set('--color-sidebar-oscuro', oscurecer(primario, 22));
    set('--color-sidebar-texto', contrasteTexto(sidebar));
    set('--color-sidebar-hover', 'rgba(255, 255, 255, 0.10)');

    // Degradado de marca reutilizable
    set('--gradiente-marca', `linear-gradient(135deg, ${primario} 0%, ${secundario} 100%)`);
};

// Lee los colores guardados en localStorage (con fallback a los defaults).
export const obtenerColoresStorage = () => {
    if (typeof localStorage === 'undefined') {
        return { colorPrimario: COLOR_PRIMARIO_DEFECTO, colorSecundario: COLOR_SECUNDARIO_DEFECTO };
    }
    return {
        colorPrimario: normalizarHex(localStorage.getItem('colorPrimario')) || COLOR_PRIMARIO_DEFECTO,
        colorSecundario: normalizarHex(localStorage.getItem('colorSecundario')) || COLOR_SECUNDARIO_DEFECTO,
    };
};

export const aplicarTemaDesdeStorage = () => {
    const { colorPrimario, colorSecundario } = obtenerColoresStorage();
    aplicarTema(colorPrimario, colorSecundario);
};

// Guarda los colores en localStorage y aplica el tema.
export const guardarColoresStorage = (colorPrimario, colorSecundario) => {
    const p = normalizarHex(colorPrimario) || COLOR_PRIMARIO_DEFECTO;
    const s = normalizarHex(colorSecundario) || COLOR_SECUNDARIO_DEFECTO;
    try {
        localStorage.setItem('colorPrimario', p);
        localStorage.setItem('colorSecundario', s);
    } catch (_) { /* ignore */ }
    aplicarTema(p, s);
};
